import type { Ref } from 'vue'
import type { GetVideosOutput } from '../../../news-feed/server/repos/video-db/video-items'
import type { GetNewsVideosRes } from '../../server/api/v2/news/index.types'
import { useReadItems } from '../useReadItems'
import type { SearchParamsNewsCategory } from '../../utils/newsCategoryTab'
import type { NewsCategory } from '../../server/models/news/news-article'

type VideosRequestsState = Partial<Record<NewsCategory, GetVideosOutput>>

/* Map title to headline value to accomodate NewsVideos not having headlines */
export const useNewsVideoHeadlines = (
  category: Ref<SearchParamsNewsCategory>
) => {
  const newsVideos = useState<VideosRequestsState>(
    `content:news:videos`,
    () => ({})
  )
  const contentDisplaySettingsStore = useContentDisplaySettingsStore()
  const { featureFlags } = storeToRefs(contentDisplaySettingsStore)
  const { sortItemsByReadStatus } = useReadItems()

  const selectedCategory = computed(() => {
    if (category?.value === undefined) return 'News'
    return category.value
  })

  const categoryHeadlines = computed(() => {
    const result = newsVideos.value[selectedCategory.value]

    return result ?? { count: 0, items: [], nextPageCursor: null }
  })

  const cursor = computed(() => categoryHeadlines.value.nextPageCursor)

  const videoHeadlines = computed(() => {
    const items = categoryHeadlines.value?.items || []
    return featureFlags.value.enableArticleReordering
      ? sortItemsByReadStatus(items)
      : items
  })

  const hasMore = computed(
    () => categoryHeadlines.value.nextPageCursor !== null
  )
  const count = computed(() => categoryHeadlines.value.count)

  const { data, status, error, execute } = useAsyncData(
    `content:news:video:${category?.value}${cursor.value}`,
    () =>
      $fetch<GetNewsVideosRes>('/api/v2/news', {
        params: {
          type: 'video',
          category: selectedCategory.value,
          cursor: cursor.value,
        },
      }),
    {
      watch: [selectedCategory],
    }
  )

  watch(data, (newHeadlines) => {
    if (!newHeadlines) return

    const previousValue = newsVideos.value[selectedCategory.value]
    newsVideos.value[selectedCategory.value] = {
      count: (previousValue?.count || 0) + newHeadlines.count,
      items: [...(previousValue?.items || []), ...newHeadlines.items],
      nextPageCursor: newHeadlines.nextPageCursor,
    }
  })

  return {
    videoHeadlines,
    status,
    error,
    hasMore,
    count,
    fetchNextPage: async () => {
      if (!hasMore.value) return
      await execute()
    },
  }
}
